import { Injectable } from '@angular/core';
import { Day } from '@app/utils/models/itinerary/day.model';
import { Experience } from '@app/utils/models/itinerary/experience.model';
import { filter, map, Observable, shareReplay, take } from 'rxjs';
import { ExperienceType } from '@ess-front/shared/models';
import { CityAccommodations } from '@app/utils/models/itinerary/city-accommodations';

@Injectable({
  providedIn: 'root',
})
export class ItinerarySelectorsService {
  getAccommodationsByCity$(days$: Observable<readonly Day[]>): Observable<readonly CityAccommodations[]> {
    return days$.pipe(
      map(days => this.getDaysAccommodations(days)),
      map(accomodations =>
        accomodations.reduce((acc: { accommodations: Experience[]; city: string }[], accommodation: Experience) => {
          const group = acc.find(group => group.city === accommodation.city);
          if (group) {
            group.accommodations.push(accommodation);
          } else {
            acc.push({ city: accommodation.city ?? '', accommodations: [accommodation] });
          }
          return acc;
        }, []),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getAlternativeExperience$(days$: Observable<readonly Day[]>, alternativeId: number): Observable<Experience> {
    return days$.pipe(
      filter(days => days.length !== 0),
      map(days => this.findAlternativeExperience(alternativeId, days)),
      take(1),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private findAlternativeExperience(alternativeId: number, days: readonly Day[]): Experience {
    for (const day of days) {
      for (const experience of day.experiences) {
        const alternative = experience.alternatives.find(alt => alt.id === alternativeId);
        if (alternative) {
          return alternative;
        }
      }
    }
    throw new Error(`Alternative experience with id ${alternativeId} not found`);
  }

  private getDaysAccommodations(days: readonly Day[]): Experience[] {
    return days.flatMap(day => day.experiences.filter(experience => experience.type === ExperienceType.ACCOMMODATION));
  }
}
